// Registrační kontrakt hlavního menu a baseline položky
// Standardní položka resetuje stack na root target, custom položka vykoná handler

/** Standardní menu položka – klik resetuje celý stack a naviguje na cílovou cestu */
export interface StandardMenuItem {
  readonly kind: 'standard';
  readonly id: string;
  readonly label: string;
  /** Cílová cesta pro navigaci (např. '/' nebo '/dev/navigation') */
  readonly path: string;
  readonly order: number;
  /**
   * Cílová root sekce hlavního menu — **stabilní, na jazyku NEZÁVISLÉ ID** (shodné s Magic
   * `<SubItem ID>`, např. `products` / `reservations`). Matching klíč proti `MenuSection.stableId`;
   * native agenda se vloží do odpovídající legacy sekce (fáze 1: na začátek). Bez `section` zůstává
   * samostatným tlačítkem vlevo v liště.
   */
  readonly section?: string;
  /** Lokalizovaný popisek sekce — zobrazí se JEN u native-only sekce (bez legacy protějšku). */
  readonly sectionLabel?: string;
  /**
   * Cílová vnořená skupina uvnitř sekce — **stabilní ID** (shodné s Magic `<SubItem ID>`, např.
   * `inventory-codebooks`). Matching proti `MenuGroupNode.stableId`; skupina se vytvoří, pokud chybí.
   * Bez `group` jde položka na úroveň sekce.
   */
  readonly group?: string;
  /** Lokalizovaný popisek skupiny — jen pro native-only skupinu. */
  readonly groupLabel?: string;
  /** GlobalName agendy pro type-ikonu (volitelné). */
  readonly key?: string;
  /** GlobalName legacy agendy, kterou tato native nahrazuje → legacy duplikát se v menu skryje. */
  readonly replacesKey?: string;
  /**
   * Oprávnění vyžadované pro zobrazení položky — klíč uzlu katalogu, stačí úroveň read (např. 'products.venua-planner').
   * Bez `permission` je položka viditelná vždy; s `permission` jen tehdy,
   * když ho přihlášený uživatel má.
   */
  readonly permission?: string;
}

/** Volitelné parametry native menu položky. */
export interface StandardMenuItemOptions {
  readonly section?: string;
  readonly sectionLabel?: string;
  readonly group?: string;
  readonly groupLabel?: string;
  readonly key?: string;
  readonly replacesKey?: string;
  readonly permission?: string;
}

/** Custom menu položka – klik vykoná registrovaný handler */
export interface CustomMenuItem {
  readonly kind: 'custom';
  readonly id: string;
  readonly label: string;
  readonly onClick: () => void;
  readonly order: number;
}

/**
 * Položka menu pocházející z Magic legacy desktopu (Phoenix-On-Top režim).
 * Klik na top-level se rozvine na dropdown s `children`; klik na leaf
 * (kind 'form' / 'click') pošle Magic control instanci požadavek na dispatch.
 */
export interface MagicMenuMainItem {
  readonly kind: 'magic';
  readonly id: string;
  readonly label: string;
  readonly order: number;
  /** Magic-side menu item ID — používá se pro dispatch akce zpět do Magicu. */
  readonly magicMenuItemId: string;
  /** Druh položky tak, jak ho označil Magic (`form`, `sub`, `click`, ...). */
  readonly magicKind: string;
  /** Pokud má položka děti (Magic SubMenuItem), zobrazí se dropdown. */
  readonly children?: readonly MagicMenuMainItem[];
}

/** Registrovatelná položka hlavního menu */
export type MainMenuItem = StandardMenuItem | CustomMenuItem | MagicMenuMainItem;

/**
 * Vytvoří standardní menu položku.
 * Po kliknutí se naviguje na cílovou cestu, čímž se celý stack resetuje.
 */
export function standardMenuItem(
  id: string,
  label: string,
  path: string,
  order: number = 0,
  options?: StandardMenuItemOptions
): StandardMenuItem {
  return {
    kind: 'standard',
    id,
    label,
    path,
    order,
    section: options?.section,
    sectionLabel: options?.sectionLabel,
    group: options?.group,
    groupLabel: options?.groupLabel,
    key: options?.key,
    replacesKey: options?.replacesKey,
    permission: options?.permission,
  };
}

/**
 * Vytvoří custom menu položku.
 * Po kliknutí se vykoná registrovaný handler bez vynuceného stack resetu.
 */
export function customMenuItem(
  id: string,
  label: string,
  onClick: () => void,
  order: number = 0
): CustomMenuItem {
  return { kind: 'custom', id, label, onClick, order };
}
